/**
 * WebRTCVideoDecoder — a VideoDecoder replacement that routes encoded
 * video frames through a WebRTC loopback connection.
 *
 * This bypasses the Safari/iOS hard limit of 2 concurrent WebCodecs
 * VideoDecoder instances by leveraging WebRTC's internal decoder pipeline.
 *
 * Architecture per instance:
 *   DummyStream -> pc1 (sender) -[RTCRtpScriptTransform]-> pc2 (receiver) -> MSTP/rVFC -> VideoFrame
 *
 * The RtpInjectorWorker replaces the outgoing RTP frame data with the
 * real encoded bitstream (Annex-B format), and the receiver's WebRTC
 * decoder decodes it. Decoded VideoFrames are extracted via
 * MediaStreamTrackProcessor (main thread or worker) or requestVideoFrameCallback.
 */
import { createWorker } from '../../../utils/workerUtils';
import { perfWorkerName } from '../perfFlags';
import { addPlayoutDelayExtension, preferCodecInSDP, setHighBandwidth, } from './annexb';
import { ensureDummyStream } from './DummyStream';
export class WebRTCVideoDecoder {
    state = 'unconfigured';
    framesOutput = 0;
    _output;
    _error;
    _pc1 = null;
    _pc2 = null;
    _worker = null;
    _ownsWorker = false;
    _reader = null;
    _active = false;
    _id = -1;
    _onLog = () => { };
    _fallbackVideo = null;
    constructor(init) {
        this._output = init.output;
        this._error = init.error ?? (() => { });
    }
    async configure(config) {
        const { sdpCodecName, decoderId, onLog, frameReaderWorker, injectorWorker, } = config;
        this._id = decoderId;
        this._onLog = onLog ?? (() => { });
        const dummyStream = ensureDummyStream();
        if (injectorWorker) {
            this._worker = injectorWorker;
            this._ownsWorker = false;
        }
        else {
            this._worker = this._createInjectorWorker();
            this._ownsWorker = true;
            this._worker.onmessage = (e) => {
                if (e.data.type === 'log')
                    this._onLog(e.data.msg);
            };
        }
        // encodedInsertableStreams enables RTCRtpScriptTransform
        // iceServers: [] skips STUN gathering — local loopback only needs host candidates
        const rtcConfig = {
            encodedInsertableStreams: true,
            iceServers: [],
            iceCandidatePoolSize: 0,
        };
        this._pc1 = new RTCPeerConnection(rtcConfig);
        this._pc2 = new RTCPeerConnection(rtcConfig);
        const pc1 = this._pc1;
        const pc2 = this._pc2;
        pc1.onicecandidate = (e) => e.candidate && pc2.addIceCandidate(e.candidate);
        pc2.onicecandidate = (e) => e.candidate && pc1.addIceCandidate(e.candidate);
        const sender = this._pc1.addTrack(dummyStream.getVideoTracks()[0], dummyStream);
        // Assign the script transform on the sender side
        sender.transform = new RTCRtpScriptTransform(this._worker, {
            streamId: decoderId,
            mode: 'live-inject',
        });
        try {
            const params = sender.getParameters();
            if (params.encodings?.length > 0) {
                params.encodings[0].maxFramerate = 60;
                params.encodings[0].maxBitrate = 20_000_000;
            }
            // biome-ignore lint/suspicious/noExplicitAny: degradationPreference not typed
            params.degradationPreference = 'maintain-framerate';
            await sender.setParameters(params);
        }
        catch (_) {
            /* some browsers reject these parameter changes */
        }
        const trackPromise = new Promise((resolve) => {
            pc2.ontrack = (e) => resolve(e.track);
        });
        // --- SDP negotiation with codec preference + high bandwidth ---
        let offer = await pc1.createOffer();
        let offerSdp = offer.sdp ?? '';
        const hasCodec = offerSdp
            .toLowerCase()
            .includes(sdpCodecName.toLowerCase());
        if (hasCodec)
            offerSdp = preferCodecInSDP(offerSdp, sdpCodecName);
        offerSdp = setHighBandwidth(offerSdp);
        offerSdp = addPlayoutDelayExtension(offerSdp);
        if (!hasCodec && decoderId === 0) {
            this._onLog(`SDP does not contain ${sdpCodecName}, using default negotiation`);
        }
        offer = { type: offer.type, sdp: offerSdp };
        await pc1.setLocalDescription(offer);
        await pc2.setRemoteDescription(offer);
        let answer = await pc2.createAnswer();
        let answerSdp = answer.sdp ?? '';
        if (hasCodec)
            answerSdp = preferCodecInSDP(answerSdp, sdpCodecName);
        answerSdp = setHighBandwidth(answerSdp);
        answerSdp = addPlayoutDelayExtension(answerSdp);
        answer = { type: answer.type, sdp: answerSdp };
        await pc2.setLocalDescription(answer);
        await pc1.setRemoteDescription(answer);
        if (decoderId === 0) {
            const cm = answerSdp.match(/a=rtpmap:\d+\s+(\S+)\//);
            if (cm)
                this._onLog(`SDP negotiated codec: ${cm[1]}`);
        }
        await Promise.all([this._waitForICE(pc1), this._waitForICE(pc2)]);
        this._onLog('ICE connected');
        const track = await trackPromise;
        // Minimize receiver jitter buffer for local loopback
        for (const rx of pc2.getReceivers()) {
            if (rx.track?.kind !== 'video')
                continue;
            // biome-ignore lint/suspicious/noExplicitAny: jitterBufferTarget not yet in lib.dom
            const r = rx;
            // Standard property (Chrome 124+, WebKit landing soon)
            if ('jitterBufferTarget' in rx)
                r.jitterBufferTarget = 0;
            // Chrome-specific fallback
            if ('playoutDelayHint' in rx)
                r.playoutDelayHint = 0;
        }
        this._active = true;
        this._setupFrameReader(track, frameReaderWorker ?? null);
        this.state = 'configured';
    }
    /**
     * Deliver a decoded VideoFrame from an external source (e.g. worker MSTP).
     * Used when Path 2 (TrackProcessorWorker) routes frames back to the main thread.
     */
    deliverFrame(frame) {
        this.framesOutput++;
        this._output(frame);
    }
    /**
     * Feed an Annex-B encoded chunk to the inject worker queue.
     * The data ArrayBuffer is transferred (detached) to the worker.
     */
    feedChunk(chunk) {
        if (!this._worker)
            throw new Error('Not configured');
        this._worker.postMessage({
            type: 'chunk',
            streamId: this._id,
            data: chunk.data,
            chunkType: chunk.type,
        }, [chunk.data]);
    }
    async getStats() {
        const result = { sender: {}, receiver: {} };
        try {
            if (this._pc1) {
                for (const [, s] of await this._pc1.getStats()) {
                    // biome-ignore lint/suspicious/noExplicitAny: WebRTC stats typing
                    const stat = s;
                    if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
                        result.sender = {
                            packetsSent: stat.packetsSent,
                            framesSent: stat.framesSent,
                        };
                    }
                }
            }
            if (this._pc2) {
                for (const [, s] of await this._pc2.getStats()) {
                    // biome-ignore lint/suspicious/noExplicitAny: WebRTC stats typing
                    const stat = s;
                    if (stat.type === 'inbound-rtp' && stat.kind === 'video') {
                        result.receiver = {
                            packetsReceived: stat.packetsReceived,
                            framesDecoded: stat.framesDecoded,
                            framesDropped: stat.framesDropped,
                        };
                    }
                }
            }
        }
        catch (_) {
            /* stats may fail if PCs are already closed */
        }
        return result;
    }
    close() {
        this._active = false;
        this.state = 'closed';
        try {
            this._reader?.cancel();
        }
        catch (_) { }
        try {
            this._pc1?.close();
        }
        catch (_) { }
        try {
            this._pc2?.close();
        }
        catch (_) { }
        if (this._ownsWorker) {
            try {
                this._worker?.terminate();
            }
            catch (_) { }
        }
        if (this._fallbackVideo) {
            this._fallbackVideo.srcObject = null;
            this._fallbackVideo.remove();
            this._fallbackVideo = null;
        }
        this._pc1 = null;
        this._pc2 = null;
        this._worker = null;
        this._reader = null;
    }
    // --- Private ---
    _createInjectorWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./RtpInjectorWorker.ts', import.meta.url), { type: 'module', name: perfWorkerName('rtpInjectorWorker') });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./RtpInjectorWorker${ext}`, import.meta.url, perfWorkerName('rtpInjectorWorker'));
    }
    /**
     * Set up decoded VideoFrame extraction from the received track.
     * Three-tier fallback:
     *   1. Main-thread MediaStreamTrackProcessor
     *   2. Worker-based MSTP (via TrackProcessorWorker)
     *   3. requestVideoFrameCallback (hidden <video> element)
     */
    _setupFrameReader(track, frameReaderWorker) {
        // Path 1: Main-thread MSTP (Chrome, future Safari)
        if (typeof MediaStreamTrackProcessor !== 'undefined') {
            try {
                const processor = new MediaStreamTrackProcessor({ track });
                this._reader = processor.readable.getReader();
                this._onLog('Frame reader: main-thread MSTP');
                void this._readLoop();
                return;
            }
            catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                this._onLog(`MSTP failed: ${msg}, trying worker path`);
            }
        }
        // Path 2: Worker MSTP (transfers track to TrackProcessorWorker)
        if (frameReaderWorker) {
            this._onLog('Frame reader: worker MSTP');
            frameReaderWorker.postMessage({ type: 'addTrack', track, streamId: this._id }, [track]);
            return;
        }
        // Path 3: requestVideoFrameCallback fallback (current Safari)
        this._onLog('Frame reader: requestVideoFrameCallback fallback');
        const video = document.createElement('video');
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.srcObject = new MediaStream([track]);
        video.style.cssText =
            'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
        document.body.appendChild(video);
        video.play();
        this._fallbackVideo = video;
        const decoder = this;
        // biome-ignore lint/suspicious/noExplicitAny: rVFC metadata typing
        function captureFrame(_now, metadata) {
            if (!decoder._active)
                return;
            try {
                const ts = Math.round((metadata.mediaTime || performance.now() / 1000) *
                    1_000_000);
                const frame = new VideoFrame(video, { timestamp: ts });
                decoder.framesOutput++;
                decoder._output(frame);
            }
            catch (_) {
                /* frame creation may fail if video not ready */
            }
            // biome-ignore lint/suspicious/noExplicitAny: rVFC not typed
            video.requestVideoFrameCallback(captureFrame);
        }
        // biome-ignore lint/suspicious/noExplicitAny: rVFC not typed
        video.requestVideoFrameCallback(captureFrame);
    }
    async _readLoop() {
        if (!this._reader)
            return;
        try {
            while (this._active) {
                const { value: frame, done } = await this._reader.read();
                if (done)
                    break;
                this.framesOutput++;
                this._output(frame);
            }
        }
        catch (e) {
            if (this._active) {
                const msg = e instanceof Error ? e.message : String(e);
                this._onLog(`Read error: ${msg}`);
                this._error(new DOMException(msg, 'OperationError'));
            }
        }
    }
    _waitForICE(pc, timeoutMs = 5000) {
        return new Promise((resolve, reject) => {
            const good = ['connected', 'completed'];
            if (good.includes(pc.iceConnectionState)) {
                resolve();
                return;
            }
            const timer = setTimeout(() => resolve(), timeoutMs);
            const handler = () => {
                const s = pc.iceConnectionState;
                if (good.includes(s)) {
                    clearTimeout(timer);
                    pc.removeEventListener('iceconnectionstatechange', handler);
                    resolve();
                }
                else if (s === 'failed') {
                    clearTimeout(timer);
                    pc.removeEventListener('iceconnectionstatechange', handler);
                    reject(new Error('ICE failed'));
                }
            };
            pc.addEventListener('iceconnectionstatechange', handler);
        });
    }
}
